/**
 * Chart-specific palette. Chart.js's imperative config can't read CSS
 * custom properties directly, so we resolve them at JS-evaluation time
 * via getters. The primary / primarySoft / categoryFirst entries follow
 * the tenant's brand colour set in `--bk-primary` (overridden by
 * TenantBrandSync). Status semantics (success / warning / danger) stay
 * as fixed hex literals - universal UX patterns we don't want a tenant
 * to break.
 */

import { useEffect, useState } from 'react'

/** Read a CSS custom property from <html>. Returns the empty string in SSR. */
function readCssVar(name: string): string {
  if (typeof document === 'undefined') return ''
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

const FALLBACK_PRIMARY_HSL = '211 100% 40%' // matches --bk-primary default

function brandHsl(): string {
  return readCssVar('--bk-primary') || FALLBACK_PRIMARY_HSL
}

export const CHART_COLORS = {
  // Each access re-reads the CSS var so a tenant brand change picks up
  // on the next chart render. Chart.js typically caches colours per-chart
  // instance, so a switch may need a chart re-render to fully apply.
  get primary()     { return `hsl(${brandHsl()})` },
  get primarySoft() { return `hsl(${brandHsl()} / 0.05)` },
  // ── Status colours: stay fixed by design (universal UX). ───────
  secondary: '#80CAEE',
  success:  '#219653',
  warning:  '#FFA70B',
  danger:   '#D34053',
  muted:    '#64748B',
  gridLight: '#F1F5F9',
  gridDark:  '#293548',
  tickLight: '#64748B',
  tickDark:  '#94A3B8',
  incomeBar:  '#00b4d8',
  expenseBar: '#6f42c1',
}

/**
 * Categorical palette - first entry tracks the tenant brand so
 * single-category charts (top expense category, lead source, etc.)
 * still feel on-brand. The rest are saturated complementary hues for
 * multi-series breakdowns.
 */
export const CATEGORY_PALETTE: readonly string[] = new Proxy<string[]>(
  ['__brand__', '#219653', '#FFA70B', '#D34053', '#6C757D'],
  {
    get(target, prop) {
      const value = (target as unknown as Record<PropertyKey, unknown>)[prop]
      if (value === '__brand__') return `hsl(${brandHsl()})`
      return value
    },
  },
)

/** Legacy analyze_statements palette - 10 saturated colors for categorical
 *  bar/pie/scatter charts. Kept verbatim so ports look identical. */
export const MODERN_CATEGORY_PALETTE = [
  'rgba(54, 162, 235, 0.8)',
  'rgba(255, 99, 132, 0.8)',
  'rgba(75, 192, 192, 0.8)',
  'rgba(255, 206, 86, 0.8)',
  'rgba(153, 102, 255, 0.8)',
  'rgba(255, 159, 64, 0.8)',
  'rgba(199, 199, 199, 0.8)',
  'rgba(83, 102, 255, 0.8)',
  'rgba(104, 210, 129, 0.8)',
  'rgba(255, 133, 54, 0.8)',
] as const

/** True if the user's theme is dark. Reads `data-theme` on <html>. */
export function isDarkMode(): boolean {
  if (typeof document === 'undefined') return false
  return document.documentElement.getAttribute('data-theme') === 'dark'
}

/**
 * Reactive dark-mode hook that observes the data-theme attribute on <html>
 * so charts re-render when the theme toggle flips it from any component.
 * useTheme() keeps per-component state and doesn't propagate, so we watch
 * the DOM instead.
 */
export function useDarkMode(): boolean {
  const [dark, setDark] = useState<boolean>(() => isDarkMode())
  useEffect(() => {
    if (typeof document === 'undefined') return
    const el = document.documentElement
    const obs = new MutationObserver(() => setDark(isDarkMode()))
    obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] })
    return () => obs.disconnect()
  }, [])
  return dark
}
